import { Client } from "@microsoft/microsoft-graph-client";

import { HttpError } from "@/types/http";
import {
  GraphAuthorization,
  GraphRequestOptions,
  GraphTenant,
} from "@/types/microsoft";
import { UserIndicator } from "@/types/user";

import { httpRequest } from "@/lib/http";

const cachedTokens: Record<GraphTenant, { token: string; expiry: number }> = {
  student: { token: "", expiry: 0 },
  employee: { token: "", expiry: 0 },
};

const clientCache: Partial<Record<GraphTenant, Client>> = {};

function createAuthProvider(tenant: GraphTenant) {
  return {
    getAccessToken: async () => {
      const now = Math.floor(Date.now() / 1000);
      const cache = cachedTokens[tenant];

      if (cache.token && now < cache.expiry - 60) {
        return cache.token;
      }

      const tenantId = getTenantId(tenant);
      const url = `${process.env.MS_AUTH_API!}/${tenantId}/oauth2/v2.0/token`;

      const params = new URLSearchParams();
      params.append("client_id", getClientId(tenant));
      params.append("client_secret", getClientSecret(tenant));
      params.append("grant_type", "client_credentials");
      params.append("scope", `${process.env.MS_GRAPH_API!}/.default`);

      const body = params.toString();
      const headers = { "Content-Type": "application/x-www-form-urlencoded" };
      const method = "POST";

      const requestInit: RequestInit = { body, headers, method };

      try {
        const response = (
          await httpRequest<GraphAuthorization>(url, requestInit)
        ).data;

        if (!response) {
          throw new Error("No response from token endpoint");
        }

        cache.token = response.access_token;
        cache.expiry = now + response.expires_in;

        return cache.token;
      } catch (err) {
        console.error("Failed to get token:", err);
        throw err;
      }
    },
  };
}

function getClientId(tenant: GraphTenant) {
  switch (tenant) {
    case "employee":
      return process.env.AZURE_EMPLOYEE_CLIENT_ID!;
    case "student":
      return process.env.AZURE_STUDENT_CLIENT_ID!;
  }
}

function getClientSecret(tenant: GraphTenant) {
  switch (tenant) {
    case "employee":
      return process.env.AZURE_EMPLOYEE_CLIENT_SECRET!;
    case "student":
      return process.env.AZURE_STUDENT_CLIENT_SECRET!;
  }
}

function getGraphClient(tenant: GraphTenant): Client {
  if (!clientCache[tenant]) {
    const authProvider = createAuthProvider(tenant);
    clientCache[tenant] = Client.initWithMiddleware({ authProvider });
  }
  return clientCache[tenant]!;
}

function getTenantId(tenant: GraphTenant) {
  switch (tenant) {
    case "employee":
      return process.env.AZURE_EMPLOYEE_TENANT_ID!;
    case "student":
      return process.env.AZURE_STUDENT_TENANT_ID!;
  }
}

export function getTenantValue(userIndicator: UserIndicator) {
  if (userIndicator === "DA" || userIndicator === "ST") return "student";
  return "employee";
}

export async function graphRequest<T>(
  path: string,
  options: GraphRequestOptions
): Promise<T> {
  try {
    let request = getGraphClient(options.tenant ?? "employee").api(path);

    if (options.beta) {
      request = request.version("beta");
    }

    if (options.headers) {
      for (const { name, value } of options.headers) {
        if (name && value) request = request.header(name, value);
      }
    }

    if (options.responseType) {
      request = request.responseType(options.responseType);
    }

    // default method is GET if not specified
    const method = (options.method ?? "GET").toLowerCase() as
      | "get"
      | "post"
      | "put"
      | "patch"
      | "delete";

    let response: T;

    if (method === "get" || method === "delete") {
      response = await request[method]();
    } else {
      // for POST, PUT, PATCH expect body
      response = await request[method](options.body ?? {});
    }

    return response;
  } catch (err: unknown) {
    let status = 500;
    let message = `Microsoft Graph API ${
      options.method ?? "GET"
    } error using path: ${path}`;

    if (typeof err === "object" && err !== null && "statusCode" in err) {
      status = (err as any).statusCode;
      message = (err as any).message;
    }

    throw new HttpError(status, message);
  }
}
